import { useAuth } from '../../contexts/AuthContext';
import React, { useEffect, useState } from 'react';
import { AlertCircle, Calendar, Download, Loader2 } from 'lucide-react';
import { downloadAuthenticatedFile } from '../../services/reportsApi';
import { buildApiUrl } from '../../utils/apiSetup';

const PfaReportView = ({ initialYear = '2024' }) => {
  const { hasPermission } = useAuth();
  const [year, setYear] = useState(initialYear);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    const load = async () => {
      setLoading(true); setError('');
      try {
        const response = await fetch(buildApiUrl(`/api/v1/reports/pfa-report?${new URLSearchParams({ year })}`), {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }, signal: controller.signal,
        });
        if (!response.ok) throw new Error('Failed to load PFA report');
        setData(await response.json());
      } catch (err) {
        if (err.name !== 'AbortError') setError(err.message);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    if (/^\d{4}$/.test(year)) load();
    return () => controller.abort();
  }, [year]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      await downloadAuthenticatedFile(`/api/v1/reports/pfa-report-pdf?${new URLSearchParams({ year })}`, `PFA_${year}_${Number(year) + 1}.pdf`);
    } catch (err) { setError(err.message || 'Failed to download PFA PDF'); }
    finally { setDownloading(false); }
  };

  const renderRow = (row, emphasis = false) => <tr key={row.label || row.department} className={emphasis ? 'bg-gray-100 font-bold' : ''}>
    <td className="border border-gray-700 p-2 font-bold">{row.label || row.department}</td>
    {row.months.map((amount, index) => <td key={index} className="border border-gray-700 p-2 text-right">₹{amount.toLocaleString('en-IN')}</td>)}
    <td className="border border-gray-700 p-2 text-right">₹{row.total.toLocaleString('en-IN')}</td>
  </tr>;

  return <div className="space-y-5">
    <div className="print:hidden flex flex-wrap items-end gap-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <Calendar className="mb-2 h-5 w-5 text-orange-600" />
      <label className="text-xs font-semibold text-gray-500">FINANCIAL YEAR
        <input value={year} onChange={(event) => setYear(event.target.value.replace(/\D/g, '').slice(0, 4))} className="mt-1 block w-28 rounded-lg border border-gray-300 px-3 py-2 text-sm font-bold text-gray-800" />
      </label>
      {hasPermission('report:export') && (
<button onClick={downloadPdf} disabled={downloading || loading || !data} className="ml-auto flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
        {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Download PDF
      </button>
)}
    </div>
    {error && <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700"><AlertCircle className="h-4 w-4" />{error}</div>}
    <div className="overflow-x-auto rounded-xl border-2 border-gray-700 bg-white p-5 shadow-lg">
      <div className="mb-4 text-center font-serif"><h3 className="text-base font-bold">PROVIDENT FUND ADVANCE AND FINAL SETTLEMENT LOAN</h3><p className="text-sm font-semibold">{data?.financial_year || `${year}-${String(Number(year) + 1).slice(-2)}`}</p></div>
      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-orange-600" /></div> : <table className="w-full min-w-[1050px] border-collapse font-mono text-xs text-black">
        <thead><tr className="bg-gray-100 font-bold"><th className="border border-gray-700 p-2">DEPARTMENT</th>{(data?.months || []).map((month) => <th key={month} className="border border-gray-700 p-2">{month}</th>)}<th className="border border-gray-700 p-2">TOTAL</th></tr></thead>
        <tbody>{(data?.rows || []).map((row) => renderRow(row))}{data?.advance_total && renderRow(data.advance_total, true)}{data?.final_settlement && renderRow(data.final_settlement, true)}{data?.total_expenditure && renderRow(data.total_expenditure, true)}</tbody>
      </table>}
    </div>
  </div>;
};

export default PfaReportView;
